import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search,
  Delete,
  ArrowBack,
  Visibility,
  Map as MapIcon,
  FavoriteBorder,
} from '@mui/icons-material';
import { catService, getImageUrl, hasValidLocation } from '../../services/api';

interface Cat {
  _id: string;
  name: string;
  breed: string;
  notes: string;
  imageUrl: string;
  location: { latitude: number; longitude: number };
  likes_pats: boolean;
  uploadedBy: { _id: string; username: string };
  createdAt: string;
}

const AdminCatList: React.FC = () => {
  const navigate = useNavigate();
  const [cats, setCats] = useState<Cat[]>([]);
  const [filteredCats, setFilteredCats] = useState<Cat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [catToDelete, setCatToDelete] = useState<Cat | null>(null);

  useEffect(() => {
    fetchCats();
  }, []);

  useEffect(() => {
    filterCats();
  }, [cats, searchQuery]);

  const fetchCats = async () => {
    try {
      const data = await catService.getAll();
      setCats(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch cats');
    } finally {
      setLoading(false);
    }
  };

  const filterCats = () => {
    if (!searchQuery) {
      setFilteredCats(cats);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = cats.filter(
      (cat) =>
        cat.name.toLowerCase().includes(query) ||
        cat.breed?.toLowerCase().includes(query) ||
        cat.notes?.toLowerCase().includes(query)
    );
    setFilteredCats(filtered);
  };

  const handleDelete = async () => {
    if (!catToDelete) return;

    try {
      await catService.delete(catToDelete._id);
      setCats(cats.filter((c) => c._id !== catToDelete._id));
      setDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete cat');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        py: 4,
        background: 'linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)',
      }}
    >
      <Container maxWidth="xl">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 3 }}
        >
          Back to Admin
        </Button>

        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Manage Cats ({cats.length})
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            placeholder="Search cats by name, breed, or notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Cats Grid */}
        {filteredCats.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">No cats found</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredCats.map((cat) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat._id}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={getImageUrl(cat.imageUrl)}
                    alt={cat.name}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/cats/${cat._id}`)}
                  />
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {cat.name}
                    </Typography>
                    {cat.breed && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {cat.breed}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      {cat.likes_pats && (
                        <Chip
                          icon={<FavoriteBorder sx={{ fontSize: 14 }} />}
                          label="Likes Pats"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {hasValidLocation(cat) && (
                        <Chip
                          icon={<MapIcon sx={{ fontSize: 14 }} />}
                          label="On Map"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      By {cat.uploadedBy.username} on{' '}
                      {new Date(cat.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/cats/${cat._id}`)}
                    >
                      View
                    </Button>
                    {hasValidLocation(cat) && (
                      <Button
                        size="small"
                        startIcon={<MapIcon />}
                        onClick={() => navigate(`/?catId=${cat._id}`)}
                      >
                        Map
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => {
                        setCatToDelete(cat);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Cat</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{catToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCatList;
